import { useMemo, useState } from 'react';

import {
  InfiniteData,
  QueryKey,
  useQuery,
  useMutation,
  useInfiniteQuery,
} from '@tanstack/react-query';

import type {
  DTOExposeNotificationsRequest,
  DTOListNotificationResponse,
  DTONotificationFilterResponse,
} from 'src/apis/dto/DTONotification';
import { RawRESTClient } from 'src/apis/service';
import { NotificationService } from 'src/apis/service/NotificationService';
import useLogin from 'src/hooks/useLogin';

const NOTIFICATION_PAGE_SIZE = 20;

export const NOTIFICATION_QUERY_KEYS = {
  all: ['notifications', 'list'] as const,
  notifications: (filterId?: string) => [...NOTIFICATION_QUERY_KEYS.all, filterId],
  filters: ['notifications', 'filters'] as const,
};

export const useGetNotificationFiltersQuery = () => {
  const { isLogin } = useLogin();
  const [service] = useState(() => new NotificationService(new RawRESTClient()));

  return useQuery<DTONotificationFilterResponse>({
    queryKey: NOTIFICATION_QUERY_KEYS.filters,
    queryFn: () => service.getFilters(),
    enabled: isLogin,
  });
};

export const useGetNotificationsInfiniteQuery = (filterId?: string) => {
  const { isLogin } = useLogin();
  const [service] = useState(() => new NotificationService(new RawRESTClient()));

  const queryKey = useMemo(() => {
    return NOTIFICATION_QUERY_KEYS.notifications(filterId);
  }, [filterId]);

  return useInfiniteQuery<
    DTOListNotificationResponse,
    unknown,
    InfiniteData<DTOListNotificationResponse>,
    QueryKey,
    string | null
  >({
    queryKey,
    async queryFn({ pageParam }) {
      return service.listNotifications({
        ...(filterId && { filterId }),
        ...(pageParam && { cursor: pageParam }),
        size: NOTIFICATION_PAGE_SIZE,
      });
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
    enabled: isLogin && !!filterId,
  });
};

/** 미노출 알림 개수. 헤더 뱃지 등에서 사용. 내부적으로 filters → list 쿼리를 캐시 공유하여 추가 호출 없음. */
export const useNotificationUnexposedCount = () => {
  const { data: filtersData } = useGetNotificationFiltersQuery();
  const defaultFilterId = filtersData?.defaultFilterId;
  const { data: notificationsData } = useGetNotificationsInfiniteQuery(defaultFilterId);

  return notificationsData?.pages[0]?.unexposedCount ?? 0;
};

export const useExposeNotificationsMutation = () => {
  const [service] = useState(() => new NotificationService(new RawRESTClient()));

  return useMutation({
    mutationFn: (data: DTOExposeNotificationsRequest) => service.exposeNotifications(data),
  });
};
